import React, { useEffect, useRef } from "react";
import {
  Container,
  Typography,
  useTheme,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_USERS } from "../../graphql/gqlUser";
import { useNavigate } from "react-router-dom";

const AllUsers = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef(false);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const { data, loading, fetchMore } = useQuery(GET_USERS, {
    variables: { pagination: { cursor: null, limit: 20 } },
    notifyOnNetworkStatusChange: true,
  });

  const users = data?.getUsers?.data || [];
  const hasMore = data?.getUsers?.hasMore;
  const nextCursor = data?.getUsers?.nextCursor;

  useEffect(() => {
    if (!loaderRef.current || !tableContainerRef.current) return;
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMore &&
          nextCursor &&
          !fetchingRef.current
        ) {
          fetchingRef.current = true;
          await fetchMore({
            variables: { pagination: { cursor: nextCursor, limit: 20 } },
            updateQuery: (prev, { fetchMoreResult }) => {
              fetchingRef.current = false;
              if (!fetchMoreResult) return prev;
              return {
                getUsers: {
                  __typename: prev.getUsers.__typename,
                  data: [
                    ...prev.getUsers.data,
                    ...fetchMoreResult.getUsers.data,
                  ],
                  nextCursor: fetchMoreResult.getUsers.nextCursor,
                  hasMore: fetchMoreResult.getUsers.hasMore,
                },
              };
            },
          }).catch(() => (fetchingRef.current = false));
        }
      },
      {
        root: tableContainerRef.current,
        threshold: 0.2,
        rootMargin: "200px 0px",
      }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, fetchMore]);

  return (
    <Container
      maxWidth="md"
      sx={{
        color: theme.palette.text.primary,
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 16 / 8,
        pt: 1,
        pb: 3,
        flex: 1,
        minHeight: 0,
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        Admin center
      </Typography>

      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          maxHeight: "90%",
          borderRadius: 2,
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6">Registered users</Typography>
        </Box>

        <TableContainer
          ref={tableContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table aria-label="users table">
            <TableBody>
              {users.map((user: any) => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => navigate(`/${user.id}/projects`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ width: "100%" }}>
                    {`${user.firstName} ${user.lastName}`}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${user.id}/projects`);
                      }}
                    >
                      Go to projects page
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow>
                <TableCell colSpan={2}>
                  <Box
                    ref={loaderRef}
                    sx={{ display: "flex", justifyContent: "center", py: 3 }}
                  >
                    {(loading || fetchingRef.current) && (
                      <CircularProgress size={28} />
                    )}
                    {!hasMore && users.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        All users loaded
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default AllUsers;
